import { supabase } from '../lib/supabase.js';

/**
 * AdminDeadlineService
 * 
 * Service for managing global college deadlines through the admin interface.
 * Provides bulk operations and data management capabilities.
 */
class AdminDeadlineService {
  /**
   * Convert a date to the correct academic year
   * @param {string} dateString - Original date string (YYYY-MM-DD)
   * @param {number} cycleStartYear - Start year of the academic cycle (e.g., 2025 for 2025-2026)
   * @returns {string|null} Updated date string
   */
  static convertToAcademicYear(dateString, cycleStartYear) {
    if (!dateString) return null;
    
    try {
      // Parse the date
      const date = new Date(dateString);
      const month = date.getMonth() + 1; // getMonth() returns 0-11
      
      // Determine which year this date should be in for the given cycle
      // July-December: Use cycle start year
      // January-June: Use cycle end year (start year + 1)
      let targetYear;
      if (month >= 7) {
        targetYear = cycleStartYear;
      } else {
        targetYear = cycleStartYear + 1;
      }
      
      // Create new date with the target year
      date.setFullYear(targetYear);
      
      // Return in YYYY-MM-DD format
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.error('Error converting date:', dateString, error);
      return dateString; // Return original if parsing fails
    }
  }

  /**
   * Bulk update deadlines to a new academic cycle
   * @param {number} targetCycleStartYear - Start year of target cycle (e.g., 2025 for 2025-2026)
   * @returns {Promise<Object>} Update results with preview
   */
  static async bulkUpdateToAcademicCycle(targetCycleStartYear) {
    try {
      // Get all colleges with deadlines
      const { data: colleges, error: fetchError } = await supabase
        .from('global_colleges')
        .select('*');

      if (fetchError) throw fetchError;

      const updates = [];
      const deadlineFields = [
        'ed_deadline', 'ed2_deadline', 'ea_deadline', 'rd_deadline',
        'finaid_deadline', 'css_deadline', 'merit_deadline', 'honors_deadline'
      ];

      for (const college of colleges) {
        const update = { 
          id: college.id,
          name: college.name,
          changes: []
        };
        let hasChanges = false;

        deadlineFields.forEach(field => {
          if (college[field]) {
            const newDate = this.convertToAcademicYear(college[field], targetCycleStartYear);
            
            // Only update if the date actually changed
            if (newDate && newDate !== college[field]) {
              update[field] = newDate;
              update.changes.push({
                field: field,
                from: college[field],
                to: newDate
              });
              hasChanges = true;
            }
          }
        });

        if (hasChanges) {
          updates.push(update);
        }
      }

      // Execute updates
      const results = {
        cycleYear: `${targetCycleStartYear}-${targetCycleStartYear + 1}`,
        total: updates.length,
        successful: 0,
        failed: 0,
        changes: [],
        errors: []
      };

      for (const update of updates) {
        const { id, name, changes, ...fields } = update;
        
        // Remove the 'changes' property before database update
        const updateFields = {};
        Object.keys(fields).forEach(key => {
          if (deadlineFields.includes(key)) {
            updateFields[key] = fields[key];
          }
        });

        const { error } = await supabase
          .from('global_colleges')
          .update(updateFields)
          .eq('id', id);

        if (error) {
          results.failed++;
          results.errors.push({ 
            collegeId: id, 
            collegeName: name,
            error: error.message 
          });
        } else {
          results.successful++;
          results.changes.push({
            collegeName: name,
            updates: changes
          });
        }
      }

      return results;
    } catch (error) {
      console.error('Error in bulk update:', error);
      throw error;
    }
  }

  /**
   * Preview what changes would be made for academic year update
   * @param {number} targetCycleStartYear - Start year of target cycle
   * @returns {Promise<Object>} Preview of changes
   */
  static async previewAcademicCycleUpdate(targetCycleStartYear) {
    try {
      const { data: colleges, error: fetchError } = await supabase
        .from('global_colleges')
        .select('*');

      if (fetchError) throw fetchError;

      const preview = {
        cycleYear: `${targetCycleStartYear}-${targetCycleStartYear + 1}`,
        totalColleges: colleges.length,
        collegesWithChanges: 0,
        totalFieldsToUpdate: 0,
        changes: []
      };

      const deadlineFields = [
        'ed_deadline', 'ed2_deadline', 'ea_deadline', 'rd_deadline',
        'finaid_deadline', 'css_deadline', 'merit_deadline', 'honors_deadline'
      ];

      for (const college of colleges) {
        const collegeChanges = {
          collegeName: college.name,
          updates: []
        };

        deadlineFields.forEach(field => {
          if (college[field]) {
            const newDate = this.convertToAcademicYear(college[field], targetCycleStartYear);
            
            if (newDate && newDate !== college[field]) {
              collegeChanges.updates.push({
                field: field.replace('_', ' ').toUpperCase(),
                from: college[field],
                to: newDate
              });
              preview.totalFieldsToUpdate++;
            }
          }
        });

        if (collegeChanges.updates.length > 0) {
          preview.changes.push(collegeChanges);
          preview.collegesWithChanges++;
        }
      }

      return preview;
    } catch (error) {
      console.error('Error generating preview:', error);
      throw error;
    }
  }

  /**
   * Legacy method - kept for backward compatibility
   * @deprecated Use bulkUpdateToAcademicCycle instead
   */
  static async bulkUpdateDeadlineYears(fromYear, toYear) {
    // Simply redirect to the new method using the toYear as cycle start
    return this.bulkUpdateToAcademicCycle(toYear);
  }

  /**
   * Get deadline statistics
   * @returns {Promise<Object>} Statistics about deadlines
   */
  static async getDeadlineStatistics() {
    try {
      const { data: colleges, error } = await supabase
        .from('global_colleges')
        .select('*');

      if (error) throw error;

      const stats = {
        totalColleges: colleges.length,
        withEarlyDeadlines: 0,
        withRDDeadline: 0,
        withFinancialAid: 0,
        missingAllDeadlines: 0,
        byDeadlineType: {
          ed_deadline: 0,
          ed2_deadline: 0,
          ea_deadline: 0,
          rd_deadline: 0,
          finaid_deadline: 0,
          css_deadline: 0,
          merit_deadline: 0,
          honors_deadline: 0
        }
      };

      colleges.forEach(college => {
        // Count by type
        Object.keys(stats.byDeadlineType).forEach(field => {
          if (college[field]) {
            stats.byDeadlineType[field]++;
          }
        });

        // Early deadlines
        if (college.ed_deadline || college.ed2_deadline || college.ea_deadline) {
          stats.withEarlyDeadlines++;
        }

        // RD deadline
        if (college.rd_deadline) {
          stats.withRDDeadline++;
        }

        // Financial aid
        if (college.finaid_deadline || college.css_deadline) {
          stats.withFinancialAid++;
        }

        // Missing all deadlines
        const hasAnyDeadline = Object.keys(stats.byDeadlineType).some(field => college[field]);
        if (!hasAnyDeadline) {
          stats.missingAllDeadlines++;
        }
      });

      return stats;
    } catch (error) {
      console.error('Error getting deadline statistics:', error);
      throw error;
    }
  }

  /**
   * Validate deadlines for logical consistency
   * @returns {Promise<Array>} Array of validation issues
   */
  static async validateDeadlines() {
    try {
      const { data: colleges, error } = await supabase
        .from('global_colleges')
        .select('*');

      if (error) throw error;

      const issues = [];

      colleges.forEach(college => {
        // Check if ED is after EA (should typically be before or same)
        if (college.ed_deadline && college.ea_deadline) {
          if (new Date(college.ed_deadline) > new Date(college.ea_deadline)) {
            issues.push({
              collegeId: college.id,
              collegeName: college.name,
              type: 'warning',
              message: 'ED deadline is after EA deadline'
            });
          }
        }

        // Check if RD is before early deadlines
        if (college.rd_deadline) {
          if (college.ed_deadline && new Date(college.rd_deadline) < new Date(college.ed_deadline)) {
            issues.push({
              collegeId: college.id,
              collegeName: college.name,
              type: 'error',
              message: 'RD deadline is before ED deadline'
            });
          }
          if (college.ea_deadline && new Date(college.rd_deadline) < new Date(college.ea_deadline)) {
            issues.push({
              collegeId: college.id,
              collegeName: college.name,
              type: 'error',
              message: 'RD deadline is before EA deadline'
            });
          }
        }

        // Check for dates in the past
        const today = new Date();
        Object.entries({
          ed_deadline: 'ED',
          ed2_deadline: 'ED2',
          ea_deadline: 'EA',
          rd_deadline: 'RD',
          finaid_deadline: 'Financial Aid',
          css_deadline: 'CSS Profile',
          merit_deadline: 'Merit',
          honors_deadline: 'Honors'
        }).forEach(([field, label]) => {
          if (college[field] && new Date(college[field]) < today) {
            issues.push({
              collegeId: college.id,
              collegeName: college.name,
              type: 'info',
              message: `${label} deadline is in the past (${college[field]})`
            });
          }
        });
      });

      return issues;
    } catch (error) {
      console.error('Error validating deadlines:', error);
      throw error;
    }
  }

  /**
   * Import deadlines from CSV or JSON
   * @param {Array} data - Array of college deadline objects
   * @returns {Promise<Object>} Import results
   */
  static async importDeadlines(data) {
    try {
      const results = {
        total: data.length,
        updated: 0,
        created: 0,
        failed: 0,
        errors: []
      };

      for (const item of data) {
        // Try to find existing college by name
        const { data: existing, error: searchError } = await supabase
          .from('global_colleges')
          .select('id')
          .eq('name', item.name)
          .single();

        if (searchError && searchError.code !== 'PGRST116') {
          results.failed++;
          results.errors.push({ college: item.name, error: searchError.message });
          continue;
        }

        if (existing) {
          // Update existing
          const { error: updateError } = await supabase
            .from('global_colleges')
            .update({
              ed_deadline: item.ed_deadline || null,
              ed2_deadline: item.ed2_deadline || null,
              ea_deadline: item.ea_deadline || null,
              rd_deadline: item.rd_deadline || null,
              finaid_deadline: item.finaid_deadline || null,
              css_deadline: item.css_deadline || null,
              merit_deadline: item.merit_deadline || null,
              honors_deadline: item.honors_deadline || null
            })
            .eq('id', existing.id);

          if (updateError) {
            results.failed++;
            results.errors.push({ college: item.name, error: updateError.message });
          } else {
            results.updated++;
          }
        } else {
          // Create new
          const { error: insertError } = await supabase
            .from('global_colleges')
            .insert({
              name: item.name,
              ed_deadline: item.ed_deadline || null,
              ed2_deadline: item.ed2_deadline || null,
              ea_deadline: item.ea_deadline || null,
              rd_deadline: item.rd_deadline || null,
              finaid_deadline: item.finaid_deadline || null,
              css_deadline: item.css_deadline || null,
              merit_deadline: item.merit_deadline || null,
              honors_deadline: item.honors_deadline || null
            });

          if (insertError) {
            results.failed++;
            results.errors.push({ college: item.name, error: insertError.message });
          } else {
            results.created++;
          }
        }
      }

      return results;
    } catch (error) {
      console.error('Error importing deadlines:', error);
      throw error;
    }
  }

  /**
   * Export all deadlines to CSV format
   * @returns {Promise<string>} CSV string
   */
  static async exportDeadlinesToCSV() {
    try {
      const { data: colleges, error } = await supabase
        .from('global_colleges')
        .select('*')
        .order('name');

      if (error) throw error;

      // Create CSV header
      const headers = [
        'College Name',
        'ED Deadline',
        'ED2 Deadline',
        'EA Deadline',
        'RD Deadline',
        'Financial Aid Deadline',
        'CSS Profile Deadline',
        'Merit Deadline',
        'Honors Deadline'
      ];

      // Create CSV rows
      const rows = colleges.map(college => [
        college.name,
        college.ed_deadline || '',
        college.ed2_deadline || '',
        college.ea_deadline || '',
        college.rd_deadline || '',
        college.finaid_deadline || '',
        college.css_deadline || '',
        college.merit_deadline || '',
        college.honors_deadline || ''
      ]);

      // Combine into CSV string
      const csv = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      return csv;
    } catch (error) {
      console.error('Error exporting deadlines:', error);
      throw error;
    }
  }
}

export default AdminDeadlineService;